import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { logout } from '@/features/authentication';
import type { EntityDetail } from '@/features/authentication';
import type { DisplayFormat } from '@/shared/utils/displayFormat';
import { STORAGE_KEYS } from '@/app/config/constants';

/**
 * The legal entity the user is working in, which every request is scoped to.
 *
 * <h2>Why it is app state and not the sidebar's own</h2>
 *
 * The choice decides what the whole application shows: the API client sends it as `X-Entity-Id` on every
 * request, and the platform narrows `AssignedEntityScope` to it — the one place Business Unit, Division,
 * Department, Location, Legal Entity and Workflow Config already scope by. A value the picker kept to
 * itself could not reach any of that.
 *
 * <h2>Exactly one, always</h2>
 *
 * There is no "all entities" state. A user assigned to several works in one at a time, and the first of
 * their entities is chosen for them until they pick another. `null` is not "all" — it is "we do not know
 * yet", before the profile has arrived, and no header is sent while it holds.
 *
 * <h2>Remembered, but never trusted</h2>
 *
 * The id is kept in `localStorage` so a reload lands where the user left off. It is validated against the
 * profile's `entityDetail` on every sign-in: an entity the user is no longer assigned to is discarded in
 * favour of the first they do hold. Without that, a revoked assignment would leave a stale id in the
 * header and every screen answering 403.
 */

/**
 * Whether the user may work in an entity: anything but one the platform reports Inactive. A profile that does
 * not say (an older platform) reads as Active.
 */
export function isEntityActive(entity: Pick<EntityDetail, 'active'>): boolean {
  return entity.active !== false;
}

export interface SelectedEntityState {
  /** The chosen entity's id, or null before the profile has been read. */
  id: number | null;
  /** The chosen entity, for the picker to render without re-deriving it. */
  name: string | null;
  /**
   * How this entity's dates and times are drawn, as the platform resolved it.
   *
   * <p>The entity's own choice, then its organization's, then the platform's default -- worked out on the
   * server so no screen has to know the chain exists and two screens cannot disagree. Null until the
   * entity has been read; every date renders in the platform's own spelling until then, rather than in a
   * format nobody chose.
   *
   * <p>It lives beside the entity because it IS the entity's: change the selected entity and the dates on
   * screen change with it, which is the behaviour that was asked for.
   */
  format: DisplayFormat | null;
  /** The entity `format` describes, so a stale one is never used for a different entity. */
  formatFor: number | null;
  /**
   * Bumped whenever the resolved format may have changed under us -- which is what saving an entity's own
   * Date Format, Time Format or Time Zone does.
   *
   * <p>A counter rather than a flag, and the previous format is deliberately left in place while the new
   * one is fetched: clearing it would drop every date on screen back to the platform's spelling for the
   * length of one request, which reads as a bug rather than as a refresh.
   */
  formatVersion: number;
}

function remembered(): number | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.SELECTED_ENTITY);
    if (raw === null || raw.trim() === '') return null;

    const parsed = Number(raw);
    return Number.isInteger(parsed) ? parsed : null;
  } catch {
    /* A private window, or blocked site data. The session simply starts on the default. */
    return null;
  }
}

function remember(id: number | null): void {
  try {
    if (id === null) {
      window.localStorage.removeItem(STORAGE_KEYS.SELECTED_ENTITY);
    } else {
      window.localStorage.setItem(STORAGE_KEYS.SELECTED_ENTITY, String(id));
    }
  } catch {
    /* Not remembering is a lesser failure than not working; the session keeps its choice in memory. */
  }
}

const initialState: SelectedEntityState = {
  id: null,
  name: null,
  format: null,
  formatFor: null,
  formatVersion: 0,
};

const selectedEntitySlice = createSlice({
  name: 'selectedEntity',
  initialState,
  reducers: {
    /**
     * Reconciles the remembered choice with what the user is actually assigned to.
     *
     * <p>Dispatched whenever the profile arrives. The remembered entity wins when it is still assigned;
     * otherwise the first assigned entity does. A user with no assignments — a Mega Admin — is left with
     * nothing selected, so no header is sent and nothing is narrowed.
     */
    entitiesReported(state, action: PayloadAction<readonly EntityDetail[] | undefined>) {
      const entities = action.payload ?? [];

      if (entities.length === 0) {
        state.id = null;
        state.name = null;
        remember(null);
        return;
      }

      const stored = remembered();
      /*
        The remembered one if still held, else the user's default entity, else the first -- each only while it is
        Active (2026-10-10): an Inactive entity is listed but never worked in, so the session opens on an Active
        one when there is any.
      */
      const usable = entities.filter(isEntityActive);
      const pool = usable.length > 0 ? usable : entities;
      const chosen =
        pool.find((entity) => entity.id === stored) ??
        pool.find((entity) => entity.defaultEntity === true) ??
        pool[0];
      if (!chosen) return;

      state.id = chosen.id;
      state.name = chosen.name;
      remember(chosen.id);
    },

    /**
     * The user picked one. Only ever an entity the picker was offering, which is their assigned set -- and never
     * an Inactive one: the picker refuses that with its message, and this ignores it should one arrive anyway.
     */
    entitySelected(state, action: PayloadAction<EntityDetail>) {
      if (!isEntityActive(action.payload)) return;
      state.id = action.payload.id;
      state.name = action.payload.name;
      remember(action.payload.id);
    },

    /**
     * The format the platform resolved for an entity.
     *
     * <p>Carries the entity it belongs to, so a format that arrives after the user has moved on is
     * ignored rather than applied to somebody else's rows.
     */
    entityFormatResolved(state, action: PayloadAction<{ entityId: number; format: DisplayFormat }>) {
      state.format = action.payload.format;
      state.formatFor = action.payload.entityId;
    },

    /**
     * The selected entity's own settings have just been saved, so its effective format may have moved.
     *
     * <p>Dispatched by the Entity master after a successful write. Without it the format is read once per
     * entity and an administrator who changes the Date Format sees the old one until they switch entities
     * or reload -- which is not what "applies immediately" means.
     */
    entityFormatStale(state) {
      state.formatVersion += 1;
    },

    /** Sign-out: the next user's choice is their own. */
    entitySelectionCleared(state) {
      state.id = null;
      state.name = null;
      state.format = null;
      state.formatFor = null;
      remember(null);
    },
  },
  extraReducers: (builder) => {
    /*
      Signing out clears it, without the auth slice knowing this one exists.

      It has to be here rather than in an effect: the shell unmounts on sign-out, so nothing in the
      layout runs to tidy up. Leaving the id behind would hand the next user of this browser the previous
      one's entity in the header until their own profile arrived.
    */
    builder.addCase(logout, (state) => {
      state.id = null;
      state.name = null;
      state.format = null;
      state.formatFor = null;
      remember(null);
    });
  },
});

export const {
  entitiesReported,
  entityFormatResolved,
  entityFormatStale,
  entitySelected,
  entitySelectionCleared,
} = selectedEntitySlice.actions;

/**
 * The format to draw this session's dates in, or null while it is still unknown.
 *
 * <p>Null while the entity it belongs to is not the one selected: a format is only ever applied to the
 * entity it was resolved for, so switching entities shows the platform's own spelling for the moment it
 * takes the new one to arrive rather than the previous entity's format.
 */
export const selectDisplayFormat = (state: { selectedEntity: SelectedEntityState }): DisplayFormat | null =>
  state.selectedEntity.formatFor !== null && state.selectedEntity.formatFor === state.selectedEntity.id
    ? state.selectedEntity.format
    : null;

export default selectedEntitySlice.reducer;
